import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, onSnapshot, updateDoc, doc } from 'firebase/firestore';
import { FaCheckCircle, FaExclamationCircle, FaUserShield, FaUserPlus } from 'react-icons/fa';

export default function Roles() {
  const [form, setForm] = useState({ name: '', mobile: '', pin: '', role: '' });
  const [employees, setEmployees] = useState([]);
  const [popup, setPopup] = useState({ message: '', type: '', visible: false });
  const [hasPermission, setHasPermission] = useState(true);

  // These are fine to read once for button gating, etc.
  const employeeData = JSON.parse(localStorage.getItem('employee'));
  const employeeRole = employeeData?.role || 'guest';

  useEffect(() => {
    // Read from localStorage *inside* the effect so we don't depend on outer vars.
    const data = JSON.parse(localStorage.getItem('employee'));
    const role = data?.role || 'guest';
    const perms = data?.permissions || [];

    if (role !== 'super_admin' && !perms.includes('add_user')) {
      setHasPermission(false);
      return;
    }

    const unsub = onSnapshot(collection(db, 'employees'), (snap) => {
      const list = snap.docs.map(d => ({ docId: d.id, ...d.data() }));
      setEmployees(list);
    });

    return () => unsub();
  }, []); // no deps needed—nothing from outer scope is used

  const showPopup = (message, type = 'success') => {
    setPopup({ message, type, visible: true });
    setTimeout(() => setPopup({ message: '', type: '', visible: false }), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, mobile, pin, role } = form;

    if (!name || !mobile || !pin || !role) {
      showPopup('All fields are required.', 'error');
      return;
    }

    try {
      await addDoc(collection(db, 'employees'), { ...form, status: 'pending' });
      setForm({ name: '', mobile: '', pin: '', role: '' });
      showPopup('Employee created successfully!');
    } catch (err) {
      console.error(err);
      showPopup('Error creating employee.', 'error');
    }
  };

  const updateStatus = async (docId, newStatus) => {
    if (employeeRole !== 'super_admin') {
      showPopup('Permission denied. Only super_admin can update status.', 'error');
      return;
    }
    try {
      await updateDoc(doc(db, 'employees', docId), { status: newStatus });
      showPopup(`User status updated to "${newStatus}"`);
    } catch (err) {
      console.error(err);
      showPopup('Failed to update status.', 'error');
    }
  };

  const predefinedRoles = [
    'super_admin', 'admin', 'support_staff', 'moderator', 'readonly_auditor',
    'artist_manager', 'booking_manager', 'booking_support', 'review_team',
    'event_manager', 'event_support', 'mentorship_manager', 'news_publisher',
    'ads_manager', 'push_notification', 'broadcast_manager', 'marketing',
    'digital_marketing', 'campaign_analyst', 'finance_team', 'payment_verifier',
    'sales', 'invoicing_team', 'analytics_viewer', 'report_manager', 'dev_ops',
    'test_admin', 'organizer_support', 'content_translator', 'affiliate_manager',
    'lead_collector', 'feedback_team'
  ];

  if (!hasPermission) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        backdropFilter: 'blur(5px)', display: 'flex', justifyContent: 'center',
        alignItems: 'center', zIndex: 999
      }}>
        <div style={{
          background: '#fff', padding: '2rem', borderRadius: '10px',
          boxShadow: '0 0 20px rgba(0,0,0,0.1)', textAlign: 'center', maxWidth: '400px'
        }}>
          <FaExclamationCircle size={36} color="#e53935" />
          <h3 style={{ margin: '1rem 0' }}>Access Denied</h3>
          <p>You do not have permission to add employees.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', fontFamily: 'sans-serif' }}>
      <h2 style={{
        fontSize: '1.8rem', marginBottom: '1rem', fontWeight: 'bold',
        display: 'flex', alignItems: 'center', gap: '0.5rem'
      }}>
        <FaUserPlus /> Create New Employee Role
      </h2>

      {popup.visible && (
        <div style={{
          marginBottom: '1rem',
          backgroundColor: popup.type === 'error' ? '#fff3cd' : '#c8e6c9',
          color: popup.type === 'error' ? '#856404' : '#1b5e20',
          padding: '1rem', borderRadius: '8px', fontWeight: 'bold',
          display: 'flex', alignItems: 'center', gap: '0.6rem'
        }}>
          {popup.type === 'error' ? <FaExclamationCircle /> : <FaCheckCircle />}
          {popup.message}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{
        background: '#fff', padding: '1.5rem', borderRadius: '10px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)', display: 'grid',
        gap: '1rem', maxWidth: '500px'
      }}>
        <input
          placeholder="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          style={inputStyle}
        />
        <input
          placeholder="Mobile"
          value={form.mobile}
          onChange={(e) => setForm({ ...form, mobile: e.target.value })}
          style={inputStyle}
        />
        <input
          placeholder="PIN"
          value={form.pin}
          onChange={(e) => setForm({ ...form, pin: e.target.value })}
          style={inputStyle}
        />
        <div>
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            style={{ ...inputStyle, marginBottom: '0.5rem' }}
          >
            <option value="">Select a role</option>
            {predefinedRoles.map((role, index) => (
              <option key={index} value={role}>{role}</option>
            ))}
          </select>
          <input
            placeholder="Or type custom role"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            style={inputStyle}
          />
        </div>
        <button type="submit" style={submitBtn}>Create Role</button>
      </form>

      <h3 style={{
        marginTop: '3rem', marginBottom: '1rem',
        display: 'flex', alignItems: 'center', gap: '0.5rem'
      }}>
        <FaUserShield /> Employee List
      </h3>

      <div style={{
        overflowX: 'auto', background: '#fff', borderRadius: '10px', border: '1px solid #ddd'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f9f9f9' }}>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Mobile</th>
              <th style={thStyle}>Role</th>
              <th style={thStyle}>PIN</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {employees.map(emp => (
              <tr key={emp.docId}>
                <td style={tdStyle}>{emp.name}</td>
                <td style={tdStyle}>{emp.mobile}</td>
                <td style={tdStyle}>{emp.role}</td>
                <td style={tdStyle}>{emp.pin}</td>
                <td style={tdStyle}>
                  <span style={{
                    padding: '0.4rem 0.7rem', borderRadius: '6px',
                    backgroundColor:
                      emp.status === 'active' ? '#c8e6c9'
                      : emp.status === 'pending' ? '#fff3cd'
                      : '#ffcdd2',
                    color:
                      emp.status === 'active' ? '#1b5e20'
                      : emp.status === 'pending' ? '#856404'
                      : '#c62828',
                    fontWeight: 'bold'
                  }}>
                    {emp.status}
                  </span>
                </td>
                <td style={tdStyle}>
                  {employeeRole === 'super_admin' ? (
                    <select
                      value={emp.status}
                      onChange={(e) => updateStatus(emp.docId, e.target.value)}
                      style={{
                        ...inputStyle, maxWidth: '120px', padding: '0.4rem',
                        backgroundColor: '#f1f1f1', border: '1px solid #ccc'
                      }}
                    >
                      <option value="pending">Pending</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  ) : (
                    <span style={{ color: '#999' }}>-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// 🎨 STYLES
const inputStyle = {
  padding: '0.75rem',
  borderRadius: '6px',
  border: '1px solid #ccc',
  fontSize: '1rem',
  width: '100%'
};

const submitBtn = {
  backgroundColor: '#e91e63',
  color: '#fff',
  padding: '0.75rem',
  borderRadius: '6px',
  border: 'none',
  fontWeight: 'bold',
  cursor: 'pointer'
};

const thStyle = {
  padding: '0.75rem',
  textAlign: 'left',
  fontWeight: 'bold',
  borderBottom: '1px solid #ccc'
};

const tdStyle = {
  padding: '0.75rem',
  borderBottom: '1px solid #eee',
  fontSize: '0.95rem'
};
